import { ref, computed, watch } from 'vue';

import { refDebounced } from '@vueuse/core';

import { message } from 'ant-design-vue';

import { useI18n } from 'vue-i18n';

import ApiCNet from '@/api/ApiCNet';

import { useBaseStationsStore } from '@/stores/baseStations';
import { MAP_MODE } from '@/components/map/consts/mapFeatureConsts';
import type { LonLat, RectangleItem } from '@/components/map/types/mapFeatureTypes';

import { showErrorMessage } from '@/components/common/errorMessage';
import { datetimeToString } from '@/components/common/dateUtils';

import { CCM_MODE } from '@/components/map/cnetConstructorMode/ccmConsts';
import type { CcmMode } from '@/components/map/cnetConstructorMode/ccmConsts';
import { useCnetOtherGrids } from '@/components/map/cnetConstructorMode/useCnetOtherGrids';
import { useCnetSubGrids } from '@/components/map/cnetConstructorMode/useCnetSubGrids';
import type {
	CcmGeneratedGridpoint,
	CcmGridpointDisplay,
	CcmSelectedData,
	CcmSelectedGridpoint,
	CcmMapData,
	MapRouteSnapshot,
} from '@/components/map/cnetConstructorMode/types/ccmTypes';


const DEFAULT_STEP = 30;


type MappedGridpoint = CcmGeneratedGridpoint & {
	label: string;
	selected: boolean;
};


const readNumberParam = (value: string | string[] | undefined): number => (
	Number(Array.isArray(value) ? value[0] : value)
);


const useMapDataCCM = (): CcmMapData => {
	const { t } = useI18n();
	const baseStationsStore = useBaseStationsStore();
	const enabled = computed(() => baseStationsStore.mode === MAP_MODE.cnetConstructor);
	const drawMode = ref<CcmMode>(CCM_MODE.list);
	const editId = ref<number>(0);
	const editMode = computed(() => editId.value > 0);
	const boundRectangles = ref<RectangleItem[]>([]);
	const boundRectanglesLoaded = ref<RectangleItem[]>([]);
	const anchorPoint = ref<LonLat | null>(null);
	// здесь step == 0, чтобы не было мусорного запроса сразу после загрузки данных при редактировании
	// если новая cnet, то step станет нормальным значением сразу при испольнении resetCnetConstructorData
	const step = ref(0);
	const stepDefault = ref(DEFAULT_STEP);
	const stepDebounced = refDebounced(step, 500);
	const title = ref('');
	const rawGridpoints = ref<CcmGeneratedGridpoint[]>([]);
	const gridpointsStatuses = ref<Map<string, boolean>>(new Map());

	const {
		showSubgridsUi,
		subGridCount,
		currentSubGrid,
		subGridsOptions,
		allowDeleteSubGrid,
		addSubGrid,
		deleteSubGrid,
		resetSubGrids,
	} = useCnetSubGrids(rawGridpoints, gridpointsStatuses);

	const {
		showOtherGrids,
		otherGridsList,
		selectedOtherGridsKeys,
		otherGridsLoading,
		otherGridpoints,
		resetOtherGrids,
	} = useCnetOtherGrids(editId);

	const resetCnetConstructorData = (onlyRemove = false): void => {
		drawMode.value = onlyRemove ? CCM_MODE.list : CCM_MODE.drawRectangles;
		editId.value = 0;
		boundRectangles.value = [];
		boundRectanglesLoaded.value = [];
		anchorPoint.value = null;
		step.value = stepDefault.value;
		title.value = 'cnet-' + datetimeToString(new Date());
		rawGridpoints.value = [];
		resetSubGrids();
		gridpointsStatuses.value.clear();
		resetOtherGrids();
	};

	const regenerateGridpointsData = (): void => {
		rawGridpoints.value = [];
		if (boundRectangles.value.length > 0 && stepDebounced.value > 0) {
			ApiCNet.cNetConstructorGenerateData(boundRectangles.value, anchorPoint.value, stepDebounced.value, data => {
				if (data.status === 'ok') {
					rawGridpoints.value = data.gridpoints ?? [];
					rawGridpoints.value.forEach(gridpoint => {
						for (let snIndex = 0; snIndex < subGridCount.value; snIndex++) {
							const key = `${gridpoint.index_lon},${gridpoint.index_lat},${snIndex}`;
							if (!gridpointsStatuses.value.has(key)) {
								gridpointsStatuses.value.set(key, true);
							}
						}
					});
				} else {
					showErrorMessage(data.errors);
				}
			});
		}
	};

	const selectedGridpoints = computed(() => rawGridpoints.value.filter(
		gridpoint => gridpointsStatuses.value.get(`${gridpoint.index_lon},${gridpoint.index_lat},${currentSubGrid.value}`) ?? false,
	));

	const getListOfGrids = (): CcmSelectedGridpoint[][] => {
		const grids: CcmSelectedGridpoint[][] = [];
		for (let snIndex = 0; snIndex < subGridCount.value; snIndex++) {
			const grid = rawGridpoints.value.filter(
				gridpoint => gridpointsStatuses.value.get(`${gridpoint.index_lon},${gridpoint.index_lat},${snIndex}`) ?? false,
			).map(gridpoint => ({
				index_lon: gridpoint.index_lon,
				index_lat: gridpoint.index_lat,
			}));
			if (grid.length > 0) {
				grids.push(grid);
			}
		}
		return grids;
	};

	const cNetConstructorSaveAndApply = (
		subNetworkIndex: number | null,
		cnetNumbers: number[] | null,
		cnetDates: string[] | null,
		doSave: boolean,
		onSuccess: () => void,
	): void => {
		ApiCNet.cNetConstructorSaveAndApply(
			editId.value,
			boundRectangles.value,
			anchorPoint.value,
			step.value,
			title.value,
			getListOfGrids(),
			subNetworkIndex, // subNetworkIndex к какому применить. null -- только сохранить
			cnetNumbers,
			cnetDates,
			doSave,
			data => {
				if (data.status === 'ok') {
					message.success(t('cnet-constructor.messages.saveSuccess'));
					onSuccess();
				} else {
					showErrorMessage(data.errors);
				}
			},
		);
	};

	const loadCNetData = (id: number): void => {
		editId.value = id;
		ApiCNet.cNetConstructorLoadData(id, data => {
			if (data.status === 'ok' && data.cnetData != null) {
				const cnetData = data.cnetData;
				title.value = cnetData.title;
				boundRectangles.value = cnetData.boundRectangles;
				boundRectanglesLoaded.value = cnetData.boundRectangles;
				anchorPoint.value = cnetData.anchorPoint;
				step.value = cnetData.step;
				rawGridpoints.value = [];
				gridpointsStatuses.value.clear();
				cnetData.subNetworks.forEach((subNetwork, snIndex) => {
					if (showSubgridsUi.value || snIndex === 0) {
						subNetwork.gridpoints.forEach(gridpoint => {
							gridpointsStatuses.value.set(`${gridpoint.index_lon},${gridpoint.index_lat},${snIndex}`, gridpoint.selected);
						});
					}
				});
				currentSubGrid.value = 0;
				subGridCount.value = showSubgridsUi.value ? cnetData.subNetworks.length : 1;
				drawMode.value = CCM_MODE.drawRectangles;
			} else {
				showErrorMessage(data.errors);
			}
		});
	};

	const cnetConstructorRoutes = ['cnet-constructor-add', 'cnet-constructor-edit', 'cnet-constructor'];

	const handleRouteChange = (to: MapRouteSnapshot, from: MapRouteSnapshot | undefined): void => {
		if (to.name === 'cnet-constructor') {
			drawMode.value = CCM_MODE.list;
		} else if (to.name === 'cnet-constructor-add') {
			stepDefault.value = DEFAULT_STEP;
			resetCnetConstructorData();
			drawMode.value = CCM_MODE.drawRectangles;
		} else if (to.name === 'cnet-constructor-edit') {
			stepDefault.value = 0;
			resetCnetConstructorData();
			loadCNetData(readNumberParam(to.params.id));
			drawMode.value = CCM_MODE.loading;
		} else if (from != null && cnetConstructorRoutes.includes(from.name)) {
			drawMode.value = CCM_MODE.list;
		}
	};

	watch([boundRectangles, anchorPoint, stepDebounced], regenerateGridpointsData);

	const calcDistanceSqr = (lon1: number, lat1: number, lon2: number, lat2: number): number => {
		return (lon1 - lon2) ** 2 + (lat1 - lat2) ** 2;
	};

	const getGp0Coords = (
		mappedGridpoints: MappedGridpoint[],
		rectangles: RectangleItem[],
	): { index_lon: number; index_lat: number } => {
		const gp0Coords = {
			index_lon: 0,
			index_lat: 0,
		};
		const selectedMapped = mappedGridpoints.filter(gridpoint => gridpoint.selected);
		if (selectedMapped.filter(gridpoint => gridpoint.index_lon === 0 && gridpoint.index_lat === 0).length > 0) {
			return gp0Coords;
		}
		const rectangle = rectangles[0];
		if (rectangle != null && selectedMapped.length > 0) {
			const rectangleCenter = {
				lon: (rectangle.point1.lon + rectangle.point2.lon) / 2,
				lat: (rectangle.point1.lat + rectangle.point2.lat) / 2,
			};
			const pointsWithDistance = selectedMapped.map(gridpoint => ({
				...gridpoint,
				distance: calcDistanceSqr(gridpoint.lon, gridpoint.lat, rectangleCenter.lon, rectangleCenter.lat),
			}));
			pointsWithDistance.sort((a, b) => a.distance - b.distance);
			const closest = pointsWithDistance[0];
			if (closest != null) {
				gp0Coords.index_lon = closest.index_lon;
				gp0Coords.index_lat = closest.index_lat;
			}
		}
		return gp0Coords;
	};

	const gridpoints = computed((): CcmGridpointDisplay[] => {
		const mappedGridpoints: MappedGridpoint[] = rawGridpoints.value.map(gridpoint => {
			const label = `${gridpoint.index_lon},${gridpoint.index_lat},${currentSubGrid.value}`;
			return {
				...gridpoint,
				label,
				selected: gridpointsStatuses.value.get(label) ?? false,
			};
		});
		const result: CcmGridpointDisplay[] = [];
		// для обратной совместимости с гридами, где нет anchorPoint. Там gp01 делаем левый верхний угол
		const gp0Coords = getGp0Coords(mappedGridpoints, boundRectangles.value);
		let gpIndex = 1;
		mappedGridpoints.forEach(gridpoint => {
			let localGpIndex;
			if ((gridpoint.index_lon === gp0Coords.index_lon && gridpoint.index_lat === gp0Coords.index_lat)) {
				localGpIndex = 1;
			} else {
				if (gridpoint.selected) {
					gpIndex++;
					localGpIndex = gpIndex;
				} else {
					localGpIndex = 0;
				}
			}
			result.push({
				...gridpoint,
				gpIndex: localGpIndex,
			});
		});
		return result;
	});

	const toggleGridpoint = (label: string): void => {
		gridpointsStatuses.value.set(label, !(gridpointsStatuses.value.get(label) ?? false));
	};

	const updateAllGridpointsStatuses = (selected: boolean): void => {
		rawGridpoints.value.forEach(gridpoint => {
			gridpointsStatuses.value.set(
				`${gridpoint.index_lon},${gridpoint.index_lat},${currentSubGrid.value}`,
				selected,
			);
		});
	};

	const SELECTED_COUNT_MAX_CCM = 63;

	const subGridSelectedCounts = computed((): number[] => {
		const counts: number[] = [];
		for (let snIndex = 0; snIndex < subGridCount.value; snIndex++) {
			const count = rawGridpoints.value.filter(
				gridpoint => gridpointsStatuses.value.get(`${gridpoint.index_lon},${gridpoint.index_lat},${snIndex}`) ?? false,
			).length;
			counts.push(count);
		}
		return counts;
	});

	const selectedData = computed((): CcmSelectedData => {
		if (selectedGridpoints.value.length == 0) {
			return {
				status: 'error',
				text: t('cnet-constructor.legend.selectedZeroCount'),
			};
		}
		if (selectedGridpoints.value.length > SELECTED_COUNT_MAX_CCM) {
			return {
				status: 'error',
				text: t('cnet-constructor.legend.selectedMaxCount', { n: selectedGridpoints.value.length, max: SELECTED_COUNT_MAX_CCM }),
			};
		}
		return {
			status: 'success',
			text: t('cnet-constructor.legend.selectedCount', { n: selectedGridpoints.value.length, max: SELECTED_COUNT_MAX_CCM }),
		};
	});

	return {
		enabled,
		drawMode,
		editMode,
		boundRectangles,
		boundRectanglesLoaded,
		anchorPoint,
		step,
		title,
		gridpoints,
		selectedData,
		subGridSelectedCounts,
		resetCnetConstructorData,
		toggleGridpoint,
		updateAllGridpointsStatuses,
		cNetConstructorSaveAndApply,
		subGridCount,
		showSubgridsUi,
		showOtherGrids,
		otherGridsList,
		selectedOtherGridsKeys,
		otherGridsLoading,
		otherGridpoints,

		currentSubGrid,
		allowDeleteSubGrid,
		subGridsOptions,
		addSubGrid,
		deleteSubGrid,
		handleRouteChange,
	};
};

export {
	useMapDataCCM,
};
